import { Filesystem, Directory } from '@capacitor/filesystem'
import { Http } from '@capacitor-community/http'
import { GetURIOptions, FetchFileOptions } from '../types/filesystem'
import { Loading, Notify } from 'quasar'

export interface UseFilesystemReturn {
  getURI: (options: GetURIOptions) => Promise<string>
  fetchFile: (fetchFileOptions: FetchFileOptions, skip_error?: boolean) => Promise<void>
  /**Fetch apk, pass url to apk and opens/installs it. */
  fetchApk: (url: string, version?: string) => Promise<void>
  /**Opens a file using cordova plugin. Sniffs the file name for the mime type.
   * Supports png, tff, png, pdf, gif, and jpg */
  openFile: (data: { path: string }) => Promise<void>
  removeDir: (data: { path: string; directory: Directory }) => Promise<void>
}

export function useFilesystem(): UseFilesystemReturn {
  const getURI = async (getURIOptions: GetURIOptions) => {
    return await Filesystem.getUri({
      path: getURIOptions.path,
      directory: getURIOptions.directory ? getURIOptions.directory : Directory.Data,
    })
      .then((data) => {
        return data.uri
      })
      .catch((error) => {
        return ''
      })
  }
  const fetchFile = async (fetchFileOptions: FetchFileOptions, skip_error = false) => {
    await Http.downloadFile({
      method: 'GET',
      filePath: fetchFileOptions.filePath,
      fileDirectory: fetchFileOptions.fileDirectory
        ? fetchFileOptions.fileDirectory
        : Directory.Data,
      url: fetchFileOptions.url,
    }).catch((error) => {
      if (!skip_error) {
        Notify.create({
          type: 'negative',
          timeout: 0,
          actions: [
            {
              icon: 'close',
              color: 'white',
              // for individual action (button):
              'aria-label': 'Dismiss',
            },
          ],
          position: 'bottom-left',
          message: `Error downloading file: ${error}`,
        })
      }
    })
  }

  const fetchApk = async (url: string, version?: string) => {
    const versionString = version ? `- ${version}` : ''
    //below is to prevent possible I/O errors that seem to come up with
    //reading/writing to capacitor apps.
    //
    const apkPath = `/neptune-update${versionString}.apk`
    const path = await getURI({ path: apkPath })
    const files = await Filesystem.readdir({
      path: '/',
      directory: Directory.Data,
    })
    if (files.files.includes(apkPath)) {
      await Filesystem.deleteFile({
        path: apkPath,
        directory: Directory.Data,
      })
    }
    Loading.show({
      message: 'Downloading updates...',
    })
    await Http.downloadFile({
      method: 'GET',
      filePath: apkPath,
      url: url,
      fileDirectory: Directory.Data,
    })
      .then(async () => {
        //@ts-expect-error cordova plugins loaded when app init
        await cordova.plugins.fileOpener2.open(path, 'application/vnd.android.package-archive', {
          error: function (e: any) {
            console.log('Error status: ' + e.status + ' - Error message: ' + e.message)
          },
          success: function () {
            console.log('file opened successfully')
          },
        })
      })
      .catch((error) => {
        console.log(`error fetching apk: ${error}`)
        Loading.hide()
        if (String(error).includes('APK Error')) {
          Notify.create({
            type: 'warning',
            timeout: 15000,
            position: 'bottom-left',
            message: 'Your connection may be weak, please try updating again shortly.',
          })
        } else {
          Notify.create({
            type: 'negative',
            timeout: 15000,
            position: 'bottom-left',
            message: `An error occurred downloading updates: ${error}.`,
          })
        }
      })
      .then(() => {
        //remove loading after download is complete
        Loading.hide()
      })
  }

  const openFile = async (data: { path: string }) => {
    const ext = getMimeType(data.path)
    if (!ext) {
      Notify.create({
        type: 'negative',
        timeout: 0,
        actions: [
          {
            icon: 'close',
            color: 'white',
            // for individual action (button):
            'aria-label': 'Dismiss',
          },
        ],
        position: 'bottom-left',
        message: 'File could not be opened: file type not supported.',
      })
      throw new Error(`Cannot open file: ${data.path}`)
    }
    const path = await getURI({ path: data.path })
    //@ts-expect-error cordova plugins loaded when app init
    await cordova.plugins.fileOpener2.open(path, ext, {
      error: function (e: any) {
        console.log('Error status: ' + e.status + ' - Error message: ' + e.message)
        Notify.create({
          type: 'negative',
          timeout: 0,
          actions: [
            {
              icon: 'close',
              color: 'white',
              // for individual action (button):
              'aria-label': 'Dismiss',
            },
          ],
          position: 'bottom-left',
          message: `File could not be opened: ${e.message}`,
        })
      },
      success: function () {
        console.log('file opened successfully')
      },
    })
  }

  /**Need to sniff extensions for valid mime types to use for fileopener2. Returns false
   * if not in 'supported mime types'
   */
  const getMimeType = (path: string) => {
    const pathSplit = path.split('.')
    //last element
    const ext = String(pathSplit[pathSplit.length - 1]).toLowerCase()

    switch (ext) {
      case 'jpg':
      case 'jpe':
      case 'jpeg':
        return 'image/jpeg'
      case 'png':
        return 'image/png'
      case 'gif':
        return 'image/gif'
      case 'pdf':
        return 'application/pdf'
      case 'tiff':
      case 'tif':
        return 'image/tiff'
      default:
        console.error(`file extension ${ext} not supported`)
        return false
    }
  }

  const removeDir = async (options: { path: string; directory: Directory | undefined }) => {
    const { path, directory } = options
    return await Filesystem.rmdir({
      path,
      directory,
      recursive: true,
    })
  }

  return {
    fetchFile,
    getURI,
    fetchApk,
    openFile,
    removeDir,
  }
}
